import { TableColumnType } from '@ui';
import { Checkbox, Tooltip } from '@ds';
import { ReactNode } from 'react';
import {
  PersonalizationSettingsCategory,
  PersonalizationSettingsEvent,
  PersonalizationEventChannelUpdateData,
  PersonalizationSettingsChannelType,
  PersonalizationSettingsChannel,
  PersonalizationCategoryChannelUpdateData,
} from '@/api/apis/communication/personalization';
import { getGroupStatus } from '@/utils/helpers';

export const getNotificationsSettingsColumns = (
  category: PersonalizationSettingsCategory,
  categoryChannelSetToggle: (data: PersonalizationCategoryChannelUpdateData) => Promise<void>,
): TableColumnType[] => {
  const getChannelsByType = (type: PersonalizationSettingsChannelType) =>
    category.notifications
      .filter((event) => event.visible)
      .map((event) => event.channels.find((ch) => ch.type === type && ch.visible))
      .filter((channel): channel is PersonalizationSettingsChannel => Boolean(channel));

  const smsStatus = getGroupStatus(getChannelsByType(PersonalizationSettingsChannelType.Sms));
  const pushStatus = getGroupStatus(getChannelsByType(PersonalizationSettingsChannelType.Push));
  const webStatus = getGroupStatus(getChannelsByType(PersonalizationSettingsChannelType.Web));

  const onCategoryChannelSetToggle = (channelType: PersonalizationSettingsChannelType, checked: boolean) => {
    categoryChannelSetToggle({ categoryName: category.name, channelType, data: { enabled: checked } });
  };

  const getCheckbox = (status: string, type: PersonalizationSettingsChannelType) => (
    <Checkbox
      size="sm"
      isSelected={status !== 'off'}
      isIndeterminate={status === 'partially_on'}
      onClick={(e) => e.stopPropagation()}
      onValueChange={(checked: boolean) => onCategoryChannelSetToggle(type, status === 'partially_on' ? true : checked)}
    />
  );

  return [
    {
      key: 'event',
      label: 'Event',
      isSortable: false,
      minWidthClassName: 'min-w-[200px] w-[30%]',
    },
    {
      key: PersonalizationSettingsChannelType.Sms,
      label: (
        <div className="flex items-center justify-center">
          {getCheckbox(smsStatus, PersonalizationSettingsChannelType.Sms)}
          <span className="uppercase">SMS</span>
        </div>
      ),
      isSortable: false,
      minWidthClassName: 'w-1/4 text-center',
    },
    {
      key: PersonalizationSettingsChannelType.Push,
      label: (
        <div className="flex items-center gap-2">
          {getCheckbox(pushStatus, PersonalizationSettingsChannelType.Push)}
          <span className="uppercase">Push</span>
          <span className="normal-case font-normal">(Mobile specific)</span>
        </div>
      ),
      isSortable: false,
      minWidthClassName: 'w-[80px] w-1/4 text-center',
    },
    {
      key: PersonalizationSettingsChannelType.Web,
      label: (
        <div className="flex items-center gap-2">
          {getCheckbox(webStatus, PersonalizationSettingsChannelType.Web)}
          <span className="uppercase">In-App WEB</span>
          <span className="normal-case font-normal">(Web specific)</span>
        </div>
      ),
      isSortable: false,
      minWidthClassName: 'w-1/4 text-center',
    },
  ];
};

export const getRenderNotificationsSettingsCell =
  (eventChannelToggle: (data: PersonalizationEventChannelUpdateData) => void) =>
  (
    { name: categoryName }: PersonalizationSettingsCategory,
    { name: eventName, label, channels }: PersonalizationSettingsEvent,
    columnKey: string,
  ): ReactNode => {
    if (columnKey === 'event') {
      return <span className="text-m-m">{label}</span>;
    }

    const channel = channels.find((ch) => ch.type === columnKey);

    if (!channel || !channel?.visible) {
      return <span className="text-m-r">N/A</span>;
    }

    const isDisabled = channel && !channel.editable;

    if (isDisabled) {
      return (
        <div className="relative">
          <Tooltip
            className="text-m-r!"
            content={
              <div className="max-w-[150px] text-center">
                Required notification can’t be turned {channel.enabled ? 'off' : 'on'}.
              </div>
            }
            showArrow
            key={channel.label}
          >
            <div>
              <Checkbox
                size="sm"
                isSelected={channel.enabled}
                isDisabled={isDisabled}
                classNames={{ wrapper: 'm-0' }}
              />
            </div>
          </Tooltip>
        </div>
      );
    }

    return (
      <Checkbox
        size="sm"
        isSelected={channel.enabled}
        classNames={{ wrapper: 'm-0' }}
        onValueChange={(checked) =>
          eventChannelToggle({
            categoryName,
            eventName,
            channelType: channel.type,
            data: { enabled: checked },
          })
        }
      />
    );
  };
